import React, { useLayoutEffect, useMemo, useState } from "react";
import { OverlayView } from "@react-google-maps/api";
import { getRightToLeftBusIcon } from "../../pages/stops/vehicleIcons.js";
import SafeOverlayView from "../map/SafeOverlayView";

const CARD_WIDTH = 220;
const CARD_HEIGHT_EST = 290;
const MARKER_GAP_ABOVE = 60;
const MARKER_GAP_BELOW = 36;
const EDGE_PAD = 10;

export function formatVehicleLastUpdate(value) {
  if (value == null || value === "") return "N/A";

  let date;
  if (typeof value === "number") {
    // Treat small numbers as unix seconds
    date = new Date(value < 1e12 ? value * 1000 : value);
  } else {
    date = new Date(value);
  }

  if (Number.isNaN(date.getTime())) return "N/A";

  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export function resolveVehicleImageUrl(vehicleData, cachedUrl, baseURL) {
  if (cachedUrl) return cachedUrl;

  const rawPath = getLatestVehicleImageLocation(vehicleData);
  if (!rawPath) return null;

  return rawPath.startsWith("http")
    ? rawPath
    : `${baseURL}/upload/image?fileName=${encodeURIComponent(rawPath)}`;
}

/** Stable fingerprint for cache invalidation when VehicleImage is replaced. */
export function getVehicleImageSourceKey(vehicleData) {
  const imageDocs =
    vehicleData?.documents?.filter((doc) => doc?.name === "VehicleImage") ||
    [];
  if (!imageDocs.length) return null;

  imageDocs.sort(
    (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0),
  );
  const doc = imageDocs[0];
  if (!doc?.location) return null;
  return `${doc.location}|${doc.createdAt || doc.updatedAt || doc.id || ""}`;
}

function getLatestVehicleImageLocation(vehicleData) {
  const imageDocs =
    vehicleData?.documents?.filter((doc) => doc?.name === "VehicleImage") ||
    [];
  if (!imageDocs.length) return null;

  imageDocs.sort(
    (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0),
  );
  return imageDocs[0]?.location || null;
}

export function isInactiveVehicleStatus(status) {
  const normalized = String(status || "")
    .trim()
    .toUpperCase();
  return normalized === "DEAD" || normalized === "INACTIVE";
}

/** Inactive/DEAD vehicles are not moving — never show a stale GPS speed. */
export function getDisplayedVehicleSpeed(speed, status) {
  if (isInactiveVehicleStatus(status)) return 0;
  const speedValue = parseFloat(speed);
  return Number.isFinite(speedValue) ? speedValue : 0;
}

function computeCardPlacement(map, position) {
  const fallback = { placeBelow: false, shiftX: 0 };

  if (!map || !position || !window.google?.maps) return fallback;

  const mapDiv = map.getDiv?.();
  if (!mapDiv) return fallback;

  const mapWidth = mapDiv.clientWidth || 0;
  const mapHeight = mapDiv.clientHeight || 0;
  if (!mapWidth || !mapHeight) return fallback;

  const bounds = map.getBounds?.();
  const projection = map.getProjection?.();
  if (!bounds || !projection) return fallback;

  const latLng = new window.google.maps.LatLng(position.lat, position.lng);
  const worldPoint = projection.fromLatLngToPoint(latLng);
  const topRight = projection.fromLatLngToPoint(bounds.getNorthEast());
  const bottomLeft = projection.fromLatLngToPoint(bounds.getSouthWest());
  if (!worldPoint || !topRight || !bottomLeft) return fallback;

  const scale = Math.pow(2, map.getZoom());
  const x =
    (worldPoint.x - bottomLeft.x) * scale;
  // y grows downward in container pixels
  const y = (worldPoint.y - topRight.y) * scale;

  const spaceAbove = y - MARKER_GAP_ABOVE;
  const spaceBelow = mapHeight - (y + MARKER_GAP_BELOW);
  const placeBelow =
    spaceAbove < CARD_HEIGHT_EST + EDGE_PAD && spaceBelow > spaceAbove;

  let shiftX = 0;
  const idealLeft = x - CARD_WIDTH / 2;
  const idealRight = x + CARD_WIDTH / 2;
  if (idealLeft < EDGE_PAD) {
    shiftX = EDGE_PAD - idealLeft;
  } else if (idealRight > mapWidth - EDGE_PAD) {
    shiftX = mapWidth - EDGE_PAD - idealRight;
  }

  return { placeBelow, shiftX };
}

/**
 * Demo-style vehicle hover card for live maps.
 * Uses FLOAT_PANE + pointer-events:none so it does not block pan/zoom/clicks.
 * Flips below / shifts horizontally when near map edges so the card stays visible.
 */
function VehicleHoverCard({
  map,
  position,
  name,
  status,
  imageUrl,
  color,
  speed,
  routeName,
  lastUpdate,
}) {
  const [placement, setPlacement] = useState({
    placeBelow: false,
    shiftX: 0,
  });

  useLayoutEffect(() => {
    if (!map || !position) return undefined;

    const updatePlacement = () => {
      setPlacement(computeCardPlacement(map, position));
    };

    updatePlacement();

    const idleListener = map.addListener?.("idle", updatePlacement);
    const boundsListener = map.addListener?.("bounds_changed", updatePlacement);

    return () => {
      if (idleListener) idleListener.remove();
      if (boundsListener) boundsListener.remove();
    };
  }, [map, position?.lat, position?.lng]);

  const cardStyle = useMemo(() => {
    const verticalStyle = placement.placeBelow
      ? { top: `${MARKER_GAP_BELOW}px`, bottom: "auto" }
      : { bottom: `${MARKER_GAP_ABOVE}px`, top: "auto" };

    return {
      position: "absolute",
      left: "50%",
      transform: `translateX(calc(-50% + ${placement.shiftX}px))`,
      backgroundColor: "#fff",
      borderRadius: "16px",
      boxShadow: "0 4px 20px rgba(0,0,0,0.18)",
      width: `${CARD_WIDTH}px`,
      padding: "16px",
      fontFamily: "Arial, sans-serif",
      zIndex: 2000,
      pointerEvents: "none",
      ...verticalStyle,
    };
  }, [placement.placeBelow, placement.shiftX]);

  if (
    !position ||
    !Number.isFinite(position.lat) ||
    !Number.isFinite(position.lng)
  ) {
    return null;
  }

  const isInactive = isInactiveVehicleStatus(status);
  const statusLabel = isInactive ? "Inactive" : "Active";
  const statusColor = isInactive ? "#F9A825" : "#008C23";
  const speedValue = getDisplayedVehicleSpeed(speed, status);
  const speedLabel = `${speedValue.toFixed(1)} Km/hr`;

  return (
    <SafeOverlayView position={position} mapPaneName={OverlayView.FLOAT_PANE}>
      <div style={cardStyle}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "12px",
            gap: "8px",
          }}
        >
          <span
            style={{
              fontWeight: 700,
              fontSize: "14px",
              color: "#111",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "120px",
            }}
          >
            {name}
          </span>
          <span
            style={{
              fontSize: "11px",
              fontWeight: 600,
              padding: "3px 10px",
              borderRadius: "20px",
              border: `1.5px solid ${statusColor}`,
              color: statusColor,
              flexShrink: 0,
            }}
          >
            {statusLabel}
          </span>
        </div>

        <div
          style={{
            width: "100%",
            height: "100px",
            borderRadius: "10px",
            overflow: "hidden",
            backgroundColor: "#f5f5f5",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "14px",
          }}
        >
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={name}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          ) : (
            <img
              src={`data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                getRightToLeftBusIcon(color || "green", 180),
              )}`}
              alt="vehicle"
              style={{ width: "80px", height: "80px" }}
            />
          )}
        </div>

        <div
          style={{
            borderTop: "1.5px solid #e0e0e0",
            marginBottom: "12px",
          }}
        />

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "8px",
            fontSize: "12px",
            color: "#444",
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9.99984 3.33301C5.40484 3.33301 1.66651 7.07134 1.66651 11.6663C1.66543 12.9525 1.96537 14.221 2.54234 15.3705C2.68401 15.6538 2.97234 15.833 3.28817 15.833H16.7107C17.0265 15.833 17.3157 15.6538 17.4573 15.3705C18.0343 14.221 18.3343 12.9525 18.3332 11.6663C18.3332 7.07134 14.5948 3.33301 9.99984 3.33301ZM14.1848 7.51801L10.8832 12.5497C10.7913 12.69 10.6727 12.8107 10.5341 12.9051C10.3955 12.9995 10.2397 13.0656 10.0755 13.0996C9.9113 13.1337 9.74202 13.135 9.57732 13.1036C9.41262 13.0721 9.25575 13.0085 9.11567 12.9163C8.9384 12.8004 8.79281 12.6422 8.69204 12.4558C8.59127 12.2695 8.5385 12.0611 8.5385 11.8493C8.5385 11.6374 8.59127 11.429 8.69204 11.2427C8.79281 11.0564 8.9384 10.8981 9.11567 10.7822L14.1473 7.48051C14.1534 7.47673 14.1608 7.47548 14.1678 7.47704C14.1748 7.4786 14.1809 7.48283 14.1848 7.48884C14.1875 7.49325 14.1889 7.49829 14.1889 7.50342C14.1889 7.50856 14.1875 7.5136 14.1848 7.51801Z"
              fill="#647490"
            />
          </svg>
          <span style={{ color: "#888" }}>Speed:</span>
          <span style={{ fontWeight: 600, color: "#111" }}>{speedLabel}</span>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "8px",
            fontSize: "12px",
            color: "#444",
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 7V12L15 14"
              stroke="#647490"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle cx="12" cy="12" r="9" stroke="#647490" strokeWidth="2" />
          </svg>
          <span style={{ color: "#888" }}>Updated:</span>
          <span
            style={{
              fontWeight: 600,
              color: "#111",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "110px",
            }}
            title={lastUpdate}
          >
            {lastUpdate || "N/A"}
          </span>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontSize: "12px",
            color: "#444",
          }}
        >
          <svg
            width="27"
            height="27"
            viewBox="0 0 27 27"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.25 25.25C7.25 23.1167 9.25 21.2064 9.25 18.85C9.25 16.4936 7.45913 14.5833 5.25 14.5833C3.04087 14.5833 1.25 16.4936 1.25 18.85C1.25 21.2064 3.25 23.1167 5.25 25.25ZM5.25 25.25H20.5833C22.4243 25.25 23.9167 23.7576 23.9167 21.9167C23.9167 20.0757 22.4243 18.5833 20.5833 18.5833H17.25M21.25 11.9167C23.25 9.78333 25.25 7.87308 25.25 5.51667C25.25 3.16025 23.4592 1.25 21.25 1.25C19.0408 1.25 17.25 3.16025 17.25 5.51667C17.25 7.87308 19.25 9.78333 21.25 11.9167ZM21.25 11.9167H16.5833C14.7424 11.9167 13.25 13.4091 13.25 15.25C13.25 17.0909 14.7424 18.5833 16.5833 18.5833H18.05"
              stroke="#647490"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span style={{ color: "#888" }}>Route:</span>
          <span
            style={{
              fontWeight: 600,
              color: "#111",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "110px",
            }}
            title={routeName}
          >
            {routeName || "N/A"}
          </span>
        </div>
      </div>
    </SafeOverlayView>
  );
}

export default React.memo(VehicleHoverCard);
